import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";

const STATUSES = ["", "pending", "approved", "credit_checked", "dispatched", "loading", "in_transit", "delivered", "cancelled"];

export default function Orders() {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["orders", status],
    queryFn: () => api.get("/orders", { params: { status: status || undefined, limit: 200 } }).then((r) => r.data),
    refetchInterval: 15000,
  });

  const items = (data?.items || []).filter(
    (o) => !q || o.code.toLowerCase().includes(q.toLowerCase()) || o.customer_name?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="orders-page">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Orders</h1>
          <p className="text-sm text-muted-foreground">All customer orders across the pipeline.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Input placeholder="Search by code / customer" value={q} onChange={(e) => setQ(e.target.value)} className="w-64" data-testid="orders-search" />
          <Select value={status} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="orders-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.filter(Boolean).map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Site</th>
                <th className="px-4 py-3 text-right">Qty (L)</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Bowser · Driver</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground">Loading...</td></tr>}
              {items.map((o) => (
                <tr key={o.id} className="border-b border-border/40 text-sm hover:bg-muted/40">
                  <td className="px-4 py-3 font-mono font-semibold">
                    <Link to={`/app/orders/${o.id}`} className="hover:text-primary" data-testid={`order-${o.code}`}>{o.code}</Link>
                    {o.emergency && <span className="ml-2 rounded bg-status-cancelled/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-status-cancelled">Emergency</span>}
                  </td>
                  <td className="px-4 py-3">{o.customer_name}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{o.site_name || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">{o.quantity_litres}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">₹{o.total_amount?.toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3"><PaymentCell order={o} /></td>
                  <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{o.bowser_code ? `${o.bowser_code} · ${o.driver_name}` : "—"}</td>
                </tr>
              ))}
              {!isLoading && items.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground">No orders match your filters</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

/** Compact payment breakdown for the Orders admin table.
 * Shows exactly what the customer paid: wallet debit, online status, or credit.
 * ─ Wallet-only paid  → "Wallet ₹X · PAID"
 * ─ Split             → "Wallet ₹X + Online ₹Y" with pending / paid pill
 * ─ Postpaid customer → "Credit ₹X · Net-N"
 * ─ Online only       → "Online ₹X" with paid / pending pill
 */
function PaymentCell({ order }) {
  const total = Number(order.total_amount || 0);
  const walletUsed = Number(order.wallet_amount_used || 0);
  const remainder = Number(order.remainder_amount || (walletUsed > 0 ? total - walletUsed : total));
  const method = (order.payment_method || "postpaid").toLowerCase();
  const status = (order.payment_status || "unpaid").toLowerCase();
  const paid = status === "paid";
  const partial = status === "partial";

  const pill = (label, tone) => (
    <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${tone}`}>{label}</span>
  );
  const pillPaid = pill("Paid", "bg-status-delivered/10 text-status-delivered");
  const pillPending = pill("Due", "bg-status-cancelled/10 text-status-cancelled");
  const pillPartial = pill("Partial", "bg-status-pending/10 text-status-pending");

  return (
    <div className="flex flex-col gap-0.5 text-xs">
      {walletUsed > 0 && (
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">Wallet</span>
          <span className="font-mono font-semibold">₹{walletUsed.toLocaleString("en-IN")}</span>
          {method === "wallet" && paid && pillPaid}
        </div>
      )}
      {method === "online" && (
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">Online</span>
          <span className="font-mono font-semibold">₹{Math.max(remainder, 0).toLocaleString("en-IN")}</span>
          {paid ? pillPaid : partial ? pillPartial : pillPending}
        </div>
      )}
      {method === "postpaid" && (
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">Credit</span>
          <span className="font-mono font-semibold">₹{Math.max(remainder, 0).toLocaleString("en-IN")}</span>
          {paid ? pillPaid : pill("Net-terms", "bg-primary/10 text-primary")}
        </div>
      )}
      {method === "wallet" && walletUsed === 0 && (
        <span className="text-muted-foreground">—</span>
      )}
    </div>
  );
}
